import React, { useCallback, useEffect, useRef, useState } from 'react';

import memoryAdapter from '@flopflip/memory-adapter';
import { useFeatureToggle } from '@flopflip/react-redux';
import { useFlopFlipConfigurator } from '@lender/utils';
import { Checkbox, Stack, Typography } from '@mui/material';
import { featureTogglesApi } from 'app/services/api';
import ApiClient from 'app/services/http/client';

import { IS_DEVELOPMENT, IS_LOCAL_DEVELOPMENT, IS_PRODUCTION } from 'constants/envs';
import { FeatureFlag, localFeatureFlags } from 'constants/featureFlags';
import { KEY_CODES } from 'constants/keyboard';
import { getKeyCode } from 'utils/keyboard';
import { renderChildrenWithProps } from 'utils/react';

import { labelRowStyles, wrapperStyles } from './FeatureToggler.styles';
import { FeatureTogglerTestIds } from './FeatureToggler.testIds';

const FeatureToggler = ({ children }) => {
  const [isShowed, setIsShowed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const isCacheClearingEnabled = useFeatureToggle(FeatureFlag.EnableCacheClearing);
  const isRequestDeduplicationEnabled = useFeatureToggle(FeatureFlag.EnableRequestDeduplication);

  const isMountedRef = useRef(false);

  const requestFunction = useCallback(async () => {
    if (IS_DEVELOPMENT && isMountedRef.current === false) {
      isMountedRef.current = true;

      return [];
    }

    try {
      const { data } = await featureTogglesApi.getFeatureToggles();

      return data ?? [];
    } finally {
      // for correct work need to make sure that FFs were put to store before we go ahead with `children`
      setTimeout(() => setIsLoaded(true), 0);
    }
  }, []);

  const { isToggled, setIsToggled } = useFlopFlipConfigurator({
    memoryAdapter,
    requestFunction,
    defaultFlags: localFeatureFlags,
  });

  const onShowToggler = useCallback(e => {
    if (getKeyCode(e) === KEY_CODES.I && e.altKey) {
      setIsShowed(prevState => !prevState);
    }
  }, []);

  // Temporary solution to fix caching of index.html
  useEffect(() => {
    if (!isLoaded || !isCacheClearingEnabled || IS_LOCAL_DEVELOPMENT) {
      return;
    }

    window.caches.keys().then(keys => keys.forEach(key => window.caches.delete(key)));
  }, [isLoaded, isCacheClearingEnabled]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    ApiClient.setIsDeduplicationEnabled(isRequestDeduplicationEnabled);
  }, [isLoaded, isRequestDeduplicationEnabled]);

  useEffect(() => {
    window.addEventListener('keydown', onShowToggler);

    return () => {
      window.removeEventListener('keydown', onShowToggler);
    };
  }, [onShowToggler]);

  const onToggleFeatures = useCallback(() => {
    setIsToggled(state => !state);
  }, [setIsToggled]);

  return (
    <>
      {!IS_PRODUCTION && isShowed && (
        <Stack sx={wrapperStyles}>
          <Stack sx={labelRowStyles}>
            <Typography className="cursor-pointer" variant="medium14">
              Turn on all
            </Typography>

            <Checkbox
              checked={isToggled}
              data-testid={FeatureTogglerTestIds.TOGGLE_ALL_CHECKBOX}
              onChange={onToggleFeatures}
            />
          </Stack>
        </Stack>
      )}

      {renderChildrenWithProps(children, { isFeatureFlagsLoaded: isLoaded })}
    </>
  );
};

export default FeatureToggler;
